import { Fragment, useMemo, useState } from 'react'
import { AlertTriangle, ChevronRight, Loader2, Pencil } from 'lucide-react'
import {
  Button,
  Card,
  CardContent,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
} from '@atrvd/saas-ui'
import { formatCurrency, formatDate } from '@dcp/shared/utils/formatters'
import { hasPermission } from '@dcp/shared'
import { ViewBankTransactionsDialog } from '@/components/banking/ViewBankTransactionsDialog'
import { ViewTransactionsIcon } from '@/components/banking/ViewTransactionsIcon'
import { UpdateLocAvailabilityDialog } from '@/components/banking/UpdateLocAvailabilityDialog'
import {
  useLocAvailability,
  useLocTransactions,
  useUpdateBorrowingBase,
  useUpdatePackBorrowingBase,
  type LocTxnScope,
} from '@/hooks/useBanking'
import { useProfile } from '@/hooks/useProfile'
import type { LocLine, LocReference } from '@/types/loc-availability'

/**
 * ARCHITECTURE NOTE — why a purpose-built <table> rather than saas-ui DataTable.
 *
 * The prototype renders GROUPED, EXPANDABLE pack rows: one parent row per
 * reference plus, when expanded, one indented child row per LOC line — all
 * sharing the same 9 columns. saas-ui `DataTable` has no native parent/child
 * (tree) expansion. Its only adjacent feature, `renderFullWidthRow`, spans a
 * single cell across the whole width, which would force us to re-implement the
 * 9-column grid inside that row to align child cells under the parent columns,
 * and to hand-wire the chevron toggle regardless. That is strictly more code
 * and worse column alignment than owning the markup. The repo already sets the
 * precedent for a bespoke semantic table (ViewBankTransactionsDialog), so this
 * page uses saas-ui `Table` primitives directly for clean parent/child column
 * alignment and a native chevron on the Reference cell.
 */

/** Aggregated parent-row figures derived from a reference's lines. */
interface ReferenceAggregate {
  borrowingBaseAmount: number
  locUsage: number
  remaining: number
  /** The sole line when a reference has exactly one, else null (multi-line "—"). */
  singleLine: LocLine | null
}

/**
 * Aggregate a reference for its parent row (DCP-167 AC.11):
 * Borrowing Base = Σ lines' amounts, LOC Usage = Σ lines' usage,
 * Remaining = Borrowing Base − LOC Usage. A single-line reference exposes that
 * line so its date/note/starting values render directly; multi-line references
 * render those fields as "—".
 */
function aggregate(reference: LocReference): ReferenceAggregate {
  const borrowingBaseAmount = reference.lines.reduce(
    (sum, line) => sum + line.borrowingBaseAmount,
    0,
  )
  const locUsage = reference.lines.reduce((sum, line) => sum + line.locUsage, 0)
  return {
    borrowingBaseAmount,
    locUsage,
    remaining: borrowingBaseAmount - locUsage,
    singleLine: reference.lines.length === 1 ? reference.lines[0] : null,
  }
}

/**
 * Sort references packs-first (AC.3), then alphabetically by name within each
 * group.
 */
function sortReferences(references: LocReference[]): LocReference[] {
  return [...references].sort((a, b) => {
    if (a.type !== b.type) return a.type === 'pack' ? -1 : 1
    return a.name.localeCompare(b.name)
  })
}

/**
 * Line of Credit Availability page (DCP-283 / DCP-167).
 *
 * Grouped/expandable references backed by the `get_loc_availability` RPC via
 * {@link useLocAvailability}. Editing a standalone facility's Borrowing Base
 * persists through {@link useUpdateBorrowingBase}; editing a pack's full
 * multi-line set persists through {@link useUpdatePackBorrowingBase} (DCP-167).
 * A reference is expandable when it has ≥2 lines. LOC Usage is the real derived
 * total from the RPC, and the "View bank transactions" drill shows the real
 * LINE OF CREDIT transactions behind it (DCP-290).
 */
export function LocAvailabilityPage() {
  /**
   * Real reference data grouped from the RPC. `isFetching` (DCP-333, distinct
   * from `isLoading`) is true for ANY fetch in flight — including a background
   * settle-time refetch after a save — and gates the drill's `expectedTotal`
   * below: see that call site for why.
   *
   * `error` (DCP-333, Presto PR #184 finding 9): read so a failed fetch can be
   * surfaced instead of silently reading as an empty table. Without it, an
   * empty `references` plus `isLoading` going false once a failed query settles
   * left NO way to tell a genuine failure apart from an org that simply has no
   * borrowing base records yet — see the error branch in the render below,
   * which must be checked BEFORE the empty-list branch for exactly that
   * reason. Unlike `CashPositionPage`, where this same hook's failure only
   * disables a drill-down affordance on a grid that keeps working from a
   * different RPC, this page has no other data source — a failure here IS the
   * whole page failing, so it gets a prominent error state rather than a side
   * notice.
   *
   * DCP-333 (PR #184 human review): `data` is taken RAW rather than through a
   * `= []` destructuring default, because the default destroyed the one piece
   * of information the error branch needs. React Query v5 keeps the last
   * successful `data` across a failed BACKGROUND refetch while setting
   * `error`, so "errored" alone does not mean "nothing to render" — and with a
   * `= []` default, `references` reads identically for "the query has no data"
   * and "the query returned an empty list". Those must not be conflated: the
   * former is a failure to show, the latter is a legitimate empty state that
   * happens to coincide with a failed refresh. Keeping the raw value lets the
   * branches below test the precise thing (`!availabilityData`) instead of the
   * lossy proxy (`references.length === 0`), which would still blank a
   * genuinely-empty-but-successfully-loaded table on a transient error.
   */
  const {
    data: availabilityData,
    isLoading,
    isFetching: availabilityFetching,
    error: availabilityError,
  } = useLocAvailability()

  /**
   * Rows to render: the fetched references, or none while there is no data.
   * Memoized on the raw value so the `?? []` fallback doesn't hand the sort
   * memo below a fresh array identity on every render.
   */
  const references = useMemo(() => availabilityData ?? [], [availabilityData])

  /**
   * A refresh failed over data we already have (DCP-333, PR #184 human review)
   * — mirrors {@link ViewBankTransactionsDialog}'s `refreshFailed`: the table
   * stays up (it is still valid, just possibly stale) and the failure is
   * reported BESIDE it rather than IN PLACE of it. This is the realistic case
   * on this page, not a hypothetical one: `useUpdateBorrowingBase` /
   * `useUpdatePackBorrowingBase` invalidate `locAvailability` on settle and the
   * save dialog lives right here, so a successful save followed by a transient
   * refetch failure is exactly the sequence that would otherwise wipe the
   * borrowing-base table the user was just editing.
   */
  const refreshFailed = !!availabilityError && availabilityData !== undefined

  /**
   * DCP-167 RBAC: only roles with `manage_loc` (accounts_payable, treasury,
   * admin) may edit the LOC borrowing base. `view_only` sees the same table but
   * the Edit affordance is hidden. manage_loc is separate from manage_banking
   * so Treasury can edit LOC without managing general banking (a temporary BA
   * constraint). Uses the same helper `RequirePermission` uses.
   */
  const { data: profile } = useProfile()
  const canManage = hasPermission(profile?.productRole, 'manage_loc')

  /** Persistence for a standalone facility's single LOC line. */
  const updateBorrowingBase = useUpdateBorrowingBase()

  /** Persistence for a pack's full multi-line borrowing base (DCP-167). */
  const updatePackBorrowingBase = useUpdatePackBorrowingBase()

  /** True while either save mutation is in flight — disables the dialog's Update button to prevent a double-submit. */
  const isSaving = updateBorrowingBase.isPending || updatePackBorrowingBase.isPending

  /** Client-side search over reference name and member facility names (AC.5). */
  const [search, setSearch] = useState('')

  /** Set of expanded pack reference ids. */
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  /**
   * Active "View bank transactions" drill-down, or null. `startingBalance` is
   * the drilled scope's Starting LOC Usage baseline, folded into the modal's
   * Total so it reconciles with the LOC Usage column (DCP-290).
   *
   * DCP-333: this carries the drilled reference's SCOPE, not its transactions.
   * The rows used to be sliced out of an org-wide feed this page held in memory;
   * they are now fetched per pop-up, so the only thing a click has to record is
   * WHICH reference was clicked (plus the figures the modal reconciles against).
   */
  const [txnDrill, setTxnDrill] = useState<{
    title: string
    /** Which reference's transactions to fetch — see {@link useLocTransactions}. */
    scope: LocTxnScope
    startingBalance: number
    // Per-facility group headings: shown when the drill spans multiple
    // facilities (a pack reference, or a multi-facility line) so each block is
    // attributable; hidden for a single facility whose name is already the
    // title (DCP-169, PR #169 review).
    showGroupHeadings: boolean
    /**
     * The LOC Usage figure the user clicked (DCP-333). Handed to the modal so it
     * can warn when the transactions it lists don't add up to it — the visible
     * tell that would have caught this ticket's bug, where a truncated feed left
     * a pop-up empty under a correct non-zero LOC Usage cell.
     */
    expectedTotal: number
  } | null>(null)

  /**
   * Real LINE OF CREDIT transactions behind the OPEN drill-down — and nothing
   * else (DCP-290, rescoped by DCP-333). Passing the scope (null while closed)
   * keeps the hook disabled until a pop-up opens and then asks the RPC for that
   * one reference's rows, instead of the org-wide feed PostgREST silently
   * truncated at 1000 rows.
   *
   * This is also why the drill BUTTONS no longer have a disabled/ready gate: the
   * DCP-290 gate existed because the modal's Total could only reconcile once a
   * prerequisite org-wide feed had resolved, and opening the modal earlier would
   * show a starting-balance-only Total that silently disagreed with the table.
   * There is no such prerequisite any more — the fetch STARTS on click, and its
   * loading and error states now live inside the modal (`isLoading` /
   * `errorMessage`), which is both a shorter path to the data and a better place
   * to explain a failure than a disabled button's tooltip.
   */
  const drill = useLocTransactions(txnDrill?.scope ?? null)

  /** Reference selected for the Update dialog, or null when closed. */
  const [editReference, setEditReference] = useState<LocReference | null>(null)

  const sorted = useMemo(() => sortReferences(references), [references])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return sorted
    return sorted.filter(
      (reference) =>
        reference.name.toLowerCase().includes(query) ||
        reference.facilities.some((facility) => facility.toLowerCase().includes(query)),
    )
  }, [sorted, search])

  /** Toggle a pack reference's expanded state. */
  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  /**
   * Persist the edited reference (DCP-167). A `pack` writes its full desired
   * line set through the replace-semantics RPC; a `facility` upserts its single
   * line. Both mutations' settle-time invalidation refetches the table.
   *
   * On success the dialog closes. On failure it is left OPEN so the user can
   * retry without re-entering their edits — the mutation's own `onError`
   * already surfaces a toast, so the error is swallowed here rather than
   * rethrown (the dialog awaits this promise but treats any rejection as
   * already-handled).
   */
  const handleSave = async (updated: LocReference) => {
    try {
      if (updated.type === 'pack') {
        await updatePackBorrowingBase.mutateAsync({
          packId: updated.id,
          lines: updated.lines,
        })
      } else {
        // A standalone facility must resolve to EXACTLY one line: useLocAvailability
        // synthesizes one when none exists, and the DB's UNIQUE(facility_id) caps it
        // at one. Anything else is an out-of-spec data anomaly — and the single-line
        // upsert below only persists lines[0], so it would silently (a) save nothing
        // for 0 lines while the dialog closed as if it succeeded, or (b) drop every
        // line past the first. Fail loudly and keep the dialog open instead.
        if (updated.lines.length !== 1) {
          toast.error('Unable to save: a facility must have exactly one line of credit.')
          return
        }
        const line = updated.lines[0]
        await updateBorrowingBase.mutateAsync({
          facilityId: updated.id,
          line: {
            name: line.name,
            borrowingBaseAmount: line.borrowingBaseAmount,
            effectiveDate: line.effectiveDate,
            note: line.note,
            startingLocUsageDate: line.startingLocUsageDate,
            startingLocUsageAmount: line.startingLocUsageAmount,
          },
        })
      }
      setEditReference(null)
    } catch {
      // Error already surfaced via the mutation's onError toast; keep the
      // dialog open so the user can retry instead of losing their edits.
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Line of Credit Availability</h1>
        <p className="text-muted-foreground">
          Borrowing base and starting usage input by pack and facility
        </p>
      </div>

      {/* Client-side search over reference and member facility names (AC.5). */}
      <SearchInput
        placeholder="Search by pack or facility name..."
        value={search}
        onChange={setSearch}
        clearable
        className="max-w-sm"
      />

      {/* Unlike the paginated Banking tables (fixed 10 rows ≈ 680px), this table
          has no pagination — so it sizes to its rows and must NOT be padded to a
          680px floor, which would add dead space and force an unnecessary page
          scroll. The empty state keeps a min-height only to center its message. */}
      <Card>
        <CardContent className="p-0">
          {/* DCP-333 (PR #184 human review): the other half of the error
              decision below — data IS present, so the table is kept and the
              failed refresh is reported beside it. Deliberately rendered
              ABOVE the branch chain rather than inside the table branch, so a
              stale-but-empty list gets the same warning instead of silently
              claiming "No borrowing base records yet." while a refresh is
              failing. It can never collide with the branches: the error branch
              requires `!availabilityData` and `isLoading` is false whenever
              data exists, so `refreshFailed` is mutually exclusive with both.
              `role="status"` — informational, never blocking. */}
          {refreshFailed && (
            <div
              role="status"
              className="m-4 flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-50 p-3 text-sm text-amber-800"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                Line of Credit availability could not be refreshed — this is the last
                successfully loaded version. Reload the page to retry.
              </span>
            </div>
          )}

          {isLoading ? (
            <div className="flex min-h-[680px] items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : availabilityError && !availabilityData ? (
            // DCP-333 (Presto PR #184 finding 9): checked BEFORE the empty-list
            // branch below — a failed `useLocAvailability` also yields
            // `references = []` (the `?? []` fallback), so an empty-list check
            // alone would render this exact failure as "No borrowing base
            // records yet.", indistinguishable from a genuinely empty org. This
            // page has no other data source (contrast `CashPositionPage`, whose
            // grid keeps working from a different RPC when this one fails), so
            // the failure gets a prominent state instead of a side notice.
            //
            // The `!availabilityData` half (PR #184 human review) is what keeps
            // this branch from HIJACKING a perfectly good table: React Query v5
            // retains `data` across a failed background refetch, and `isLoading`
            // is false because data exists, so gating on the error alone
            // replaced a populated borrowing-base table with this block — worst
            // of all right after a save, whose invalidation triggers exactly
            // that refetch. `!availabilityData` (not `references.length === 0`)
            // is the precise test: it fires only when there is genuinely nothing
            // to show, and never for a legitimately empty list. That case is
            // covered by `refreshFailed` above instead.
            <div className="flex min-h-[680px] items-center justify-center p-4">
              <div
                role="status"
                className="flex max-w-md items-start gap-2 rounded-md border border-amber-500/50 bg-amber-50 p-4 text-sm text-amber-800"
              >
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <div className="space-y-1">
                  <p className="font-medium">Unable to load Line of Credit availability</p>
                  <p>{availabilityError.message} Reload the page to retry.</p>
                </div>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex min-h-[680px] items-center justify-center">
              <p className="text-sm text-muted-foreground">
                {/* Distinguish "nothing to show at all" (e.g. a new org with no
                    active facilities/packs) from "the search matched nothing" —
                    mentioning "this filter" when the search box is empty would
                    be misleading. */}
                {search.trim()
                  ? 'No borrowing base records match this filter.'
                  : 'No borrowing base records yet.'}
              </p>
            </div>
          ) : (
            <div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reference</TableHead>
                    <TableHead>Borrowing Base Amount</TableHead>
                    <TableHead>LOC Usage</TableHead>
                    <TableHead>Remaining Credit Availability</TableHead>
                    <TableHead>Date Effective</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Starting LOC Usage Date</TableHead>
                    <TableHead>Starting LOC Usage Amount</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((reference) => {
                    const agg = aggregate(reference)
                    // Expandable when a reference has ≥2 lines (matches the
                    // prototype's `multiLine = cfg.lines.length > 1`). A pack or
                    // facility with ≤1 line renders as a single row whose values
                    // come from its single line (via `agg.singleLine`).
                    const isExpandable = reference.lines.length > 1
                    const isOpen = expanded.has(reference.id)
                    const single = agg.singleLine

                    return (
                      <Fragment key={reference.id}>
                        {/* PARENT ROW — aggregates + expand/edit affordances. */}
                        <TableRow className="group">
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {isExpandable ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => toggleExpanded(reference.id)}
                                  aria-expanded={isOpen}
                                  aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${reference.name}`}
                                  className="h-6 w-6"
                                >
                                  <ChevronRight
                                    className={cn(
                                      'h-4 w-4 transition-transform',
                                      isOpen && 'rotate-90',
                                    )}
                                  />
                                </Button>
                              ) : (
                                // Spacer keeps standalone-facility names aligned
                                // with expandable references' names.
                                <span className="inline-block h-6 w-6" />
                              )}
                              <span className="font-medium">{reference.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">
                            {formatCurrency(agg.borrowingBaseAmount)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {formatCurrency(agg.locUsage)}
                              {/* View bank transactions — shown ALWAYS on every
                                  reference row (packs included), not on hover.
                                  The drill covers the WHOLE reference: every line
                                  of a multi-line pack, not just one (DCP-290). */}
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  setTxnDrill({
                                    title: reference.name,
                                    // A reference row is a pack or a standalone
                                    // facility — never a line — so it scopes by the
                                    // reference's OWN id and lets the RPC gather
                                    // every contributing line server-side (DCP-333;
                                    // this replaces the client-side flatMap over a
                                    // line-keyed feed). Scoping by reference is also
                                    // what keeps a fabricated id out of the request:
                                    // useLocAvailability SYNTHESIZES a line with
                                    // `id: ''` for a facility that has no loc_lines
                                    // row yet, and a per-line lookup would have sent
                                    // that '' on as a uuid filter.
                                    scope:
                                      reference.type === 'pack'
                                        ? { kind: 'pack', id: reference.id }
                                        : { kind: 'facility', id: reference.id },
                                    // Parent scope: sum the baselines of all lines
                                    // so the modal Total = the parent LOC Usage.
                                    startingBalance: reference.lines.reduce(
                                      (sum, l) => sum + (l.startingLocUsageAmount ?? 0),
                                      0,
                                    ),
                                    // A pack parent's title is the pack name → show
                                    // per-facility headings (even a single member).
                                    showGroupHeadings: reference.type === 'pack',
                                    // The aggregated figure rendered in this very
                                    // cell (DCP-333).
                                    expectedTotal: agg.locUsage,
                                  })
                                }
                                title="View bank transactions"
                                aria-label={`View bank transactions for ${reference.name}`}
                                className="h-6 w-6"
                              >
                                <ViewTransactionsIcon className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">
                            {formatCurrency(agg.remaining)}
                          </TableCell>
                          {/* DCP-167 AC.11: single-line references show their line's
                              values; multi-line references show "—". */}
                          <TableCell>
                            {single?.effectiveDate ? formatDate(single.effectiveDate) : '—'}
                          </TableCell>
                          <TableCell>{single?.note ? single.note : '—'}</TableCell>
                          <TableCell>
                            {single?.startingLocUsageDate
                              ? formatDate(single.startingLocUsageDate)
                              : '—'}
                          </TableCell>
                          <TableCell>
                            {single && single.startingLocUsageAmount != null
                              ? formatCurrency(single.startingLocUsageAmount)
                              : '—'}
                          </TableCell>
                          <TableCell>
                            {/* DCP-167: both facilities and packs are editable —
                                a facility upserts its single line, a pack replaces
                                its full multi-line set through the Update dialog.
                                RBAC gate: only manage_loc roles (accounts_payable,
                                treasury, admin) get the Edit affordance; without it
                                the cell renders nothing (null), leaving an empty
                                Actions cell like the child rows, so the column width
                                stays consistent either way. */}
                            {canManage ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => setEditReference(reference)}
                                aria-label={`Edit ${reference.name}`}
                                // Always visible (not hover-only) so the edit
                                // affordance is obvious on every row.
                                className="h-6 w-6"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>

                        {/* CHILD ROWS — one per line, shown while the pack is expanded. */}
                        {isExpandable &&
                          isOpen &&
                          reference.lines.map((line) => (
                            <TableRow key={line.id} className="group bg-muted/30">
                              <TableCell>
                                {/* Indented member facilities, comma-joined.
                                    Block (not inline) so pl-8 indents EVERY
                                    wrapped line — a long facility list stays
                                    left-aligned instead of the wrapped lines
                                    staircasing back to the cell's left edge. */}
                                <div className="pl-8 text-sm text-muted-foreground">
                                  {line.facilities.join(', ')}
                                </div>
                              </TableCell>
                              <TableCell>
                                {formatCurrency(line.borrowingBaseAmount)}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  {formatCurrency(line.locUsage)}
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() =>
                                      setTxnDrill({
                                        title: line.facilities.join(', ') || line.name,
                                        // Child rows exist only for expandable
                                        // references (≥2 lines), i.e. packs, whose
                                        // lines are always REAL loc_lines rows — the
                                        // synthesized `id: ''` line belongs to a
                                        // standalone facility, which has exactly one
                                        // line and so never expands. A line-scoped
                                        // drill therefore can't send an empty id
                                        // (DCP-333).
                                        scope: { kind: 'line', id: line.id },
                                        startingBalance: line.startingLocUsageAmount ?? 0,
                                        // A multi-facility line's title lists them
                                        // all → per-facility headings disambiguate;
                                        // a single-facility line's title IS that
                                        // facility, so no heading.
                                        showGroupHeadings: line.facilities.length > 1,
                                        // The per-line figure rendered in this very
                                        // cell (DCP-333).
                                        expectedTotal: line.locUsage,
                                      })
                                    }
                                    title="View bank transactions"
                                    aria-label={`View bank transactions for ${line.name}`}
                                    className="h-6 w-6"
                                  >
                                    <ViewTransactionsIcon className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                              <TableCell>
                                {formatCurrency(line.borrowingBaseAmount - line.locUsage)}
                              </TableCell>
                              <TableCell>
                                {line.effectiveDate ? formatDate(line.effectiveDate) : '—'}
                              </TableCell>
                              <TableCell>{line.note ? line.note : '—'}</TableCell>
                              <TableCell>
                                {line.startingLocUsageDate
                                  ? formatDate(line.startingLocUsageDate)
                                  : '—'}
                              </TableCell>
                              <TableCell>
                                {line.startingLocUsageAmount != null
                                  ? formatCurrency(line.startingLocUsageAmount)
                                  : '—'}
                              </TableCell>
                              {/* Child rows have no per-line Actions — edits happen
                                  through the parent reference's Update dialog. */}
                              <TableCell />
                            </TableRow>
                          ))}
                      </Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* "View bank transactions" drill-down modal (DCP-283). Rows are the real
          LINE OF CREDIT transactions behind the line's LOC Usage (DCP-290),
          fetched for this scope only while the modal is open (DCP-333) — hence
          the loading/error/reconciliation props below. */}
      {txnDrill && (
        <ViewBankTransactionsDialog
          open
          onClose={() => setTxnDrill(null)}
          referenceName={txnDrill.title}
          groups={drill.data ?? []}
          // `isLoading`, NOT `isPending`: in React Query v5 `isPending` only means
          // "no data yet", so it is also true for a DISABLED query — it would read
          // as loading for a scope nobody asked for. `isLoading` is defined as
          // `isPending && isFetching`, i.e. true only while the first real fetch
          // for this scope is in flight. It is also false during a background
          // refetch over cached rows, so saving an inline Note refreshes the feed
          // without blanking the table the user is editing.
          isLoading={drill.isLoading}
          // Only surface the error when there is genuinely nothing to show (DCP-333).
          // A failed background refetch sets `isError` while React Query v5 KEEPS the
          // previous `data` in cache — gating on `isError` alone would blank a
          // populated table on a transient error, worst of all right after a Notes
          // save (its invalidation triggers exactly this refetch). With `!drill.data`
          // the stale-but-valid rows stay visible and the query recovers on the next
          // successful refetch; a genuine first-load failure still has no data, so it
          // still shows the message.
          errorMessage={drill.isError && !drill.data ? drill.error.message : undefined}
          // The other half of the errorMessage decision above (DCP-333): nothing
          // cached shows the error IN PLACE of the rows (errorMessage), something
          // cached keeps the rows and warns BESIDE them (refreshFailed) — a failed
          // background refetch is never silent either way.
          refreshFailed={drill.isError && !!drill.data}
          // Suppress the modal's own missing-transactions check while the
          // reference table itself is fetching (DCP-333). `startingLocUsageAmount`
          // (used by both this reference's LOC Usage aggregate and its baseline)
          // is written to the cache optimistically by useUpdateBorrowingBase /
          // useUpdatePackBorrowingBase, deliberately ahead of the derived LOC
          // Usage recompute that only lands on the settle-time refetch. During
          // that ~0.5-2s window `agg.locUsage` (this drill's expectedTotal) can
          // transiently disagree with an empty transaction list for a facility
          // with none — a false alarm, not a real data-integrity signal. `undefined`
          // already means "skip the check" to the modal, so we simply withhold the
          // figure until the source query settles.
          expectedTotal={availabilityFetching ? undefined : txnDrill.expectedTotal}
          showGroupHeadings={txnDrill.showGroupHeadings}
          startingBalance={txnDrill.startingBalance}
        />
      )}

      {/* "Update Line of Credit Availability" modal (DCP-283 / DCP-167). */}
      <UpdateLocAvailabilityDialog
        open={editReference !== null}
        reference={editReference}
        onClose={() => setEditReference(null)}
        onSave={handleSave}
        isSaving={isSaving}
      />
    </div>
  )
}
